import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  EVENT_TYPES,
  HISTORY_PAGE_SIZE,
  clampHistoryPage,
  historyErrorText,
  historyProjectParam,
  useApprovalHistory,
  type ApprovalHistoryItem,
  type HistoryDeliveryState,
} from "../lib/approvals";
import { sentAtLabel } from "./PresendNotice";
import { EmptyState, TableSkeleton } from "./states";

const RESULT_LABEL: Record<ApprovalHistoryItem["result"], string> = {
  approved: "Angenommen",
  rejected: "Abgelehnt",
  rejected_after_send: "Nach Versand abgelehnt",
};

const RESULT_STYLE: Record<ApprovalHistoryItem["result"], string> = {
  approved: "bg-success-bg text-success",
  rejected: "bg-danger-bg text-danger",
  rejected_after_send: "bg-warning-bg text-warning",
};

const STATE_LABEL: Record<ApprovalHistoryItem["delivery"]["meta"]["status"], string> = {
  delivered: "zugestellt",
  failed: "fehlgeschlagen",
  suppressed: "unterdrückt (Duplikat)",
  none: "kein Nachweis",
};

/** Hand-over state to Meta/Google — only what the delivery log proves. "Zugestellt"
 *  appears only with a delivery record; the pre-report mark is shown as what it is. */
function Delivery({ delivery }: { delivery: ApprovalHistoryItem["delivery"] }) {
  if (!delivery.applicable) return <span>—</span>;
  const line = (name: string, st: HistoryDeliveryState) => (
    <div className={st.status === "failed" ? "text-danger" : undefined}>
      {name}: {STATE_LABEL[st.status]}
      {st.status === "delivered" && st.at ? ` (${sentAtLabel(st.at)})` : ""}
    </div>
  );
  return (
    <div className="space-y-0.5">
      {line("Meta", delivery.meta)}
      {line("Google", delivery.google)}
      {delivery.presendAt && (
        <div
          className="text-xs text-text-tertiary"
          title="Vor der Entscheidung zur Meldung an Meta/Google übergeben – dort nicht rücknehmbar"
        >
          Vorab zur Meldung übergeben ({sentAtLabel(delivery.presendAt)})
        </div>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { v: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="font-medium text-text-secondary">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md border border-border-primary bg-bg-primary px-2 text-text-primary focus:border-border-focus focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function HistoryFilters(props: {
  status: string;
  decision: string;
  eventType: string;
  onStatus: (v: string) => void;
  onDecision: (v: string) => void;
  onEventType: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        label="Ergebnis"
        value={props.status}
        options={[
          { v: "", label: "Alle" },
          { v: "approved", label: "Angenommen" },
          { v: "rejected", label: "Abgelehnt" },
        ]}
        onChange={props.onStatus}
      />
      <Select
        label="Entschieden"
        value={props.decision}
        options={[
          { v: "", label: "Alle" },
          { v: "manual", label: "Von Hand" },
          { v: "automatic", label: "Automatisch (Regel, KI)" },
        ]}
        onChange={props.onDecision}
      />
      <Select
        label="Event-Typ"
        value={props.eventType}
        options={[{ v: "", label: "Alle Typen" }, ...EVENT_TYPES.map((t) => ({ v: t, label: t }))]}
        onChange={props.onEventType}
      />
    </div>
  );
}

export function HistoryTable({ items }: { items: ApprovalHistoryItem[] }) {
  if (items.length === 0) {
    return <EmptyState title="Noch keine entschiedenen Fälle" body="Für diese Filter gibt es keine Einträge." />;
  }
  const th = "px-3 py-2.5 font-medium text-text-secondary";
  const td = "px-3 py-2.5 text-text-secondary";
  return (
    <div className="overflow-hidden rounded-md border border-border-primary">
      <table className="w-full text-sm">
        <thead className="border-b border-border-primary bg-bg-secondary text-left">
          <tr>
            <th className={th}>Entschieden am</th>
            <th className={th}>Entscheider</th>
            <th className={th}>Ergebnis</th>
            <th className={th}>Meta/Google</th>
            <th className={th}>Typ</th>
            <th className={th}>Projekt</th>
            <th className={th}>Kunde</th>
            <th className={`${th} text-right`}>Wert</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border-secondary last:border-0">
              <td className={td}>{it.decidedAt ? sentAtLabel(it.decidedAt) : "—"}</td>
              <td className={td}>
                <span className="text-text-primary">{it.decider.label}</span>{" "}
                {it.decider.detail && <span className="text-xs text-text-tertiary">{it.decider.detail} </span>}
                <span className="text-xs text-text-tertiary">
                  {it.decider.kind === "person" ? "Von Hand" : "Automatisch"}
                </span>
              </td>
              <td className="px-3 py-2.5">
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${RESULT_STYLE[it.result]}`}
                >
                  {RESULT_LABEL[it.result]}
                </span>
              </td>
              <td className={td}>
                <Delivery delivery={it.delivery} />
              </td>
              <td className={td}>
                {it.eventType}
                {it.gateType === "merge_review" ? " (Merge)" : ""}
              </td>
              <td className={td}>{it.projectName}</td>
              <td className="px-3 py-2.5 font-mono text-xs text-text-secondary">
                {[it.firstName, it.lastName].filter(Boolean).join(" ") || it.email || "—"}
              </td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-primary">
                {it.dealValue === null
                  ? "—"
                  : `${it.dealValue.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${it.currency ?? ""}`.trim()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HistoryPager({
  offset,
  limit,
  total,
  onPage,
}: {
  offset: number;
  limit: number;
  total: number;
  onPage: (offset: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const btn =
    "rounded-md border border-border-primary bg-bg-primary px-3 py-1 text-sm font-medium text-text-secondary hover:bg-bg-hover disabled:opacity-40";
  return (
    <div className="flex items-center justify-between text-sm text-text-secondary">
      <span className="font-mono tabular-nums">
        {from}–{to} von {total}
      </span>
      <div className="flex gap-2">
        <button type="button" className={btn} disabled={offset <= 0} onClick={() => onPage(Math.max(0, offset - limit))}>Zurück</button>
        <button type="button" className={btn} disabled={offset + limit >= total} onClick={() => onPage(offset + limit)}>Weiter</button>
      </div>
    </div>
  );
}

/** The history could not be loaded: say why, instead of a skeleton that never ends. */
export function HistoryError({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-md border border-danger bg-danger-bg px-4 py-3 text-sm text-danger">
      Die Historie konnte nicht geladen werden: {message}
    </div>
  );
}

/** Reiter „Historie": decided cases, newest first. Every filter and the page live in the
 *  URL (project/eventType shared with the queue; hstatus/hdecision/hpage are the tab's own),
 *  so they survive a switch to „Offen" and back. */
export function ApprovalHistoryTab() {
  const [params, setParams] = useSearchParams();
  const project = historyProjectParam(params.get("project"));
  const eventType = params.get("eventType") || undefined;
  const status = params.get("hstatus") ?? "";
  const decision = params.get("hdecision") ?? "";
  const wantedPage = Math.max(1, parseInt(params.get("hpage") ?? "1", 10) || 1);
  // Past the last page (old link, list shrank): the first answer carries the total, then we hold back.
  const [lastTotal, setLastTotal] = useState<number | undefined>(undefined);
  const page = clampHistoryPage(wantedPage, lastTotal);
  const offset = (page - 1) * HISTORY_PAGE_SIZE;
  const { data, error } = useApprovalHistory({ project, eventType, status, decision }, offset);
  if (data && data.total !== lastTotal) setLastTotal(data.total);
  // any filter change goes back to the first page
  const setParam = (key: string) => (v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(key, v);
    else p.delete(key);
    p.delete("hpage");
    setParams(p, { replace: true });
  };
  const setOffset = (next: number) => {
    const p = new URLSearchParams(params);
    if (next > 0) p.set("hpage", String(next / HISTORY_PAGE_SIZE + 1));
    else p.delete("hpage");
    setParams(p, { replace: true });
  };

  return (
    <div className="px-6 pb-8">
      <div className="mx-auto max-w-content space-y-4">
        <HistoryFilters
          status={status}
          decision={decision}
          eventType={eventType ?? ""}
          onStatus={setParam("hstatus")}
          onDecision={setParam("hdecision")}
          onEventType={setParam("eventType")}
        />
        {error ? (
          <HistoryError message={historyErrorText(error)} />
        ) : !data ? (
          <TableSkeleton rows={6} />
        ) : (
          <HistoryTable items={data.items} />
        )}
        {data && data.total > 0 && (
          <HistoryPager offset={offset} limit={HISTORY_PAGE_SIZE} total={data.total} onPage={setOffset} />
        )}
      </div>
    </div>
  );
}
